import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { waLink, orderWhatsAppMessage } from "../lib/whatsapp";
import { CheckCircle2, Copy, Check, MessageCircle } from "lucide-react";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadOrder = async () => {
      try {
        const raw = sessionStorage.getItem(
          "printmytees_order_confirmation_v1"
        );
        const details = raw ? JSON.parse(raw) : null;

        if (
          !details?.order_number ||
          String(details.order_number).toUpperCase() !== String(orderNumber).toUpperCase() ||
          !details?.mobile
        ) {
          throw new Error("Confirmation session expired");
        }

        const response = await api.post("/orders/confirmation", {
          order_number: details.order_number,
          mobile: details.mobile,
        });

        if (!cancelled) {
          setOrder(response.data);
          sessionStorage.removeItem("printmytees_order_confirmation_v1");
        }
      } catch {
        if (!cancelled) setOrder(false);
      }
    };

    loadOrder();

    return () => {
      cancelled = true;
    };
  }, [orderNumber]);

  const copyOrderNumber = async () => {
    if (!order?.order_number) return;

    try {
      await navigator.clipboard.writeText(String(order.order_number));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  if (order === null) {
    return (
      <div className="container-pmt py-20" data-testid="order-loading">
        Loading…
      </div>
    );
  }

  if (order === false) {
    return (
      <div className="container-pmt py-20" data-testid="order-not-found">
        <div className="max-w-xl">
          <h1 className="display-h2">Order confirmation unavailable</h1>
          <p className="mt-3 text-stone-700">
            We could not load this order confirmation. You can return to the
            shop, check order tracking, or contact us on WhatsApp.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/shop" className="btn-outline-pmt">
              Continue Shopping
            </Link>
            <Link to="/track-order" className="btn-outline-pmt">
              Track Your Order
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const waMsg = orderWhatsAppMessage(order);
  const isPaid = order.payment_status === "paid";
  const hasGiftItems = Array.isArray(order.items) &&
    order.items.some((item) => String(item?.product_type || "").toLowerCase() === "gift");
  const continueShoppingPath = hasGiftItems
    ? "/shop?category=Personalised%20Gifts"
    : "/shop";

  let paymentStatus = "UNKNOWN";

  if (order.payment_status === "paid") {
    paymentStatus = "PAID";
  } else if (order.payment_status === "pending") {
    paymentStatus = "PENDING";
  } else if (order.payment_status) {
    paymentStatus = String(order.payment_status).toUpperCase();
  }

  return (
    <div
      className="container-pmt py-16"
      data-testid="order-confirmation"
    >
      <div className="max-w-2xl">
        <CheckCircle2
          size={40}
          className="text-[#D95328]"
          aria-hidden="true"
        />

        <h1 className="display-h2 mt-3">
          {isPaid ? "Order Confirmed!" : "Thank you for your order!"}
        </h1>

        <p className="mt-3 text-stone-700">
          {isPaid
            ? "Your payment has been received successfully. Your order is confirmed and our team will begin processing it shortly."
            : "We've received your order. Our team will contact you on WhatsApp shortly."}
        </p>

        <div className="mt-8 card-pmt p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="overline">Order Number</div>

              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className="font-mono font-semibold break-all"
                  data-testid="order-number"
                >
                  {order.order_number}
                </span>
                <button
                  type="button"
                  onClick={copyOrderNumber}
                  className="inline-flex items-center gap-1 rounded border border-[#E6DFD5] px-2 py-1 text-xs font-medium text-stone-700 hover:bg-[#F8F5F0] focus:outline-none focus:ring-2 focus:ring-[#D95328]/30"
                  aria-label="Copy order number"
                  title="Copy order number"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div>
              <div className="overline">Status</div>
              <div className="mt-1">
                <span className="inline-flex rounded-full bg-[#F3EDE2] px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-stone-800">
                  {order.status}
                </span>
              </div>
            </div>

            <div>
              <div className="overline">Payment Status</div>
              <div className="mt-1">
                <span className="inline-flex rounded-full bg-[#F3EDE2] px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-stone-800">
                  {paymentStatus}
                </span>
              </div>
            </div>

            <div>
              <div className="overline">Payment Method</div>
              <div className="mt-1 break-words">{order.payment_method}</div>
            </div>
          </div>

          <div className="mt-6">
            <div className="overline">Delivery Details</div>
            <p className="mt-1 text-sm text-stone-600">
              Your delivery details were saved securely with your order.
            </p>
          </div>

          <div className="mt-6">
            <div className="overline">Items</div>

            <ul className="mt-2 divide-y divide-[#E6DFD5]">
              {order.items.map((it, i) => (
                <li key={i} className="py-3 text-sm">
                  <div className="font-semibold">
                    {it.product_name} × {it.quantity}
                  </div>

                  <div className="mt-1 text-xs text-stone-500">
                    {[
                      it.size && `Size: ${it.size}`,
                      it.color && `Colour: ${it.color}`,
                      it.printing_method && `Print: ${it.printing_method}`,
                      it.design_id && `Design: ${it.design_id}`,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>

                  {(it.custom_text ||
                    it.custom_name ||
                    it.custom_number) && (
                    <div className="mt-1 rounded border border-[#E6DFD5] bg-[#F8F5F0] p-2 text-xs text-stone-600">
                      <span className="font-semibold text-stone-700">
                        Customisation:
                      </span>{" "}
                      {[
                        it.custom_name && `Name: ${it.custom_name}`,
                        it.custom_number && `Number: ${it.custom_number}`,
                        it.custom_text && `Text: "${it.custom_text}"`,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 p-4 bg-[#F3EDE2] text-xs text-stone-700 border border-[#E6DFD5]">
            <b>Next steps:</b>{" "}
            {isPaid
              ? "Your payment is confirmed. Our team will contact you on WhatsApp if any artwork or customization details are required."
              : "Our team will contact you on WhatsApp shortly."}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={waLink(waMsg)}
            target="_blank"
            rel="noreferrer"
            className="btn-whatsapp"
            data-testid="order-wa"
          >
            <MessageCircle size={14} />
            Chat with PrintMyTees
          </a>

          <Link
            to="/track-order"
            className="btn-outline-pmt"
            data-testid="order-track"
          >
            Track Your Order
          </Link>

          <Link
            to={continueShoppingPath}
            className="btn-outline-pmt"
            data-testid="order-continue"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
